import { DateTime } from 'luxon';
import type { ListExperienceEventInput } from '@/types/gql.generated';
import type { DecoratedEvent } from '../types';

export const RANGE_QUERY_PARAM = 'range';

export type DateTimeRange = {
  startDate: DateTime;
  endDate: DateTime;
};

/** A range with either side left open. */
export type DateTimeWindow = {
  start?: DateTime;
  end?: DateTime;
};

// A timed event's lastEndDate is a bare date in its own zone; use the instant.
export const getEventLastEnd = (event: DecoratedEvent): DateTime =>
  (event.isAllDay
    ? event.lastEndDate
    : (event.lastEndDateTime ?? event.lastEndDate)) ?? event.endDate;

export const eventOverlapsWindow = (
  event: DecoratedEvent,
  window: DateTimeWindow
): boolean => {
  const { start, end } = window;
  // A reversed window describes no time at all, so nothing overlaps it. Needed
  // explicitly: an event spanning both endpoints satisfies each bound check
  // below on its own. resolveViewWindow leaves a content window reversed on
  // purpose when a view's range misses the schedule entirely.
  if (start && end && start > end) {
    return false;
  }
  if (end && event.startDate > end) {
    return false;
  }
  if (!start) {
    return true;
  }
  const eventEnd = getEventLastEnd(event);
  // An all-day end is exclusive — midnight of the day after the last day the
  // event covers — so one landing exactly on the window start sits entirely
  // before it. A timed end is an inclusive instant and still overlaps.
  return event.isAllDay ? eventEnd > start : eventEnd >= start;
};

export const eventOverlapsRange = (
  event: DecoratedEvent,
  range: DateTimeRange
): boolean =>
  eventOverlapsWindow(event, { start: range.startDate, end: range.endDate });

/**
 * The events a window doesn't show. Either side may be left open; a reversed
 * window (start after end) describes no time at all, so everything falls
 * outside it — the honest answer for a view rendering nothing.
 */
export const getEventsOutsideWindow = <E extends DecoratedEvent>(
  events: E[],
  window: DateTimeWindow
): E[] => events.filter((event) => !eventOverlapsWindow(event, window));

// The last calendar day an event covers. An all-day end is exclusive — midnight
// of the day after the last covered day, an invariant createDecoratedMagicEvent
// holds for every decoded event — so it steps back a day. A timed end is already
// an inclusive instant.
const getLastCoveredDay = (event: DecoratedEvent): DateTime => {
  const end = getEventLastEnd(event);
  return event.isAllDay ? end.minus({ days: 1 }) : end;
};

// The closest event a window doesn't show, preferring the next one ahead of it.
export const getNearestEventOutsideWindow = <E extends DecoratedEvent>(
  events: E[],
  window: DateTimeWindow
): E | undefined => {
  const outside = getEventsOutsideWindow(events, window);
  const { end } = window;
  const ahead = end ? outside.filter((event) => event.startDate > end) : [];

  if (ahead.length > 0) {
    return ahead.reduce((a, b) => (b.startDate < a.startDate ? b : a));
  }

  return outside.length > 0
    ? outside.reduce((a, b) => (b.startDate > a.startDate ? b : a))
    : undefined;
};

export const getEventsDateRange = (
  events: DecoratedEvent[]
): DateTimeRange | undefined => {
  if (events.length === 0) {
    return undefined;
  }

  const startDate = events
    .map((event) => event.startDate)
    .reduce((a, b) => (b < a ? b : a));
  const lastDay = events
    .map(getLastCoveredDay)
    .reduce((a, b) => (b > a ? b : a));

  return {
    startDate,
    // A zero-length all-day event steps its end back before its own start; the
    // span is still that single day.
    endDate: lastDay < startDate ? startDate : lastDay,
  };
};

// A span in long form, collapsing whatever the two ends share:
// "January 6, 2025" / "January 6 – 28, 2025" / "January 6 – February 28, 2025".
// A span crossing New Year carries the year on BOTH ends — one trailing year
// would date the start to the end's year. Mirrors ResponsiveWeekGrid's
// formatDateRange, which titles the week sheets.
export const formatDateTimeRange = ({
  startDate,
  endDate,
}: DateTimeRange): string => {
  if (startDate.hasSame(endDate, 'day')) {
    return startDate.toFormat('MMMM d, yyyy');
  }

  const spansYears = !startDate.hasSame(endDate, 'year');
  const start = startDate.toFormat(spansYears ? 'MMMM d, yyyy' : 'MMMM d');
  const end = startDate.hasSame(endDate, 'month')
    ? endDate.toFormat('d, yyyy')
    : endDate.toFormat('MMMM d, yyyy');

  return `${start} – ${end}`;
};

export const rangeToFilters = (
  range: DateTimeRange
): ListExperienceEventInput => ({
  startDate: range.startDate.toISODate() ?? undefined,
  endDate: range.endDate.toISODate() ?? undefined,
});

export const serializeRange = (range: DateTimeRange): string =>
  `${range.startDate.toISODate()}_${range.endDate.toISODate()}`;

const isFourDigitYear = ({ year }: DateTime): boolean =>
  year >= 1000 && year <= 9999;

export const parseRange = (
  raw: string | null | undefined
): DateTimeRange | undefined => {
  if (!raw) {
    return undefined;
  }

  const [startISO, endISO] = raw.split('_');

  if (!startISO || !endISO) {
    return undefined;
  }

  // The app runs luxon with `Settings.throwOnInvalid` (see src/index.tsx), so
  // an out-of-range component in the URL's range param (a hand-edited or
  // corrupted link, e.g. a month of 44) throws instead of producing an
  // invalid DateTime the `isValid` check below could catch (Sentry
  // WEB-CLIENT-2B1). Same pattern as `createEntryDate`.
  let startDate: DateTime;
  let endDate: DateTime;
  try {
    startDate = DateTime.fromISO(startISO).startOf('day');
    endDate = DateTime.fromISO(endISO).endOf('day');
  } catch {
    return undefined;
  }

  if (!startDate.isValid || !endDate.isValid) {
    return undefined;
  }

  if (endDate < startDate) {
    return undefined;
  }

  // Luxon reads the extended-year form ("+020261-08-19"), and a year typed or
  // corrupted past four digits would reach the events query as a date the
  // server rejects with a 400 (Sentry WEB-CLIENT-2CP). Years below 1000 are the
  // same class: a partially typed year.
  if (!isFourDigitYear(startDate) || !isFourDigitYear(endDate)) {
    return undefined;
  }

  return { startDate, endDate };
};
